import { useEffect, useMemo, useRef, useState } from 'react';
import { ImpulseSpinner } from 'react-spinners-kit';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useWorkspace } from 'hooks/useWorkspace';
import cn from 'classnames';

import dropDownIcon from 'assets/dropDownIcon.svg';
import noReviews from 'assets/noReviews.png';
import noReviewsFound from 'assets/noReviewsFound.png';
import ReviewsMagnifyIcon from 'assets/ReviewsMagnifyIcon.svg';
import ReviewsSearchTimer from 'assets/ReviewsSearchTimer.svg';

import {
  getArchivedReviewsAction,
  getDesignReviewCountsAction,
  getDesignReviewHistoryAction,
} from 'Redux/Actions/designReviewActions';

import { DESIGN_REVIEW_STATUS, PLATFORM } from 'utlis/constants';

import { SCORE_FILTERS } from './Overview';
import ReviewRow from './ReviewRow';

// Time filter constants
const TIME_FILTERS = {
  THIS_MONTH: 'This month',
  PAST_24_HOURS: 'Past 24 hours',
  LAST_7_DAYS: 'Last 7 days',
  LAST_14_DAYS: 'Last 14 days',
  LAST_30_DAYS: 'Last 30 days',
  ALL_TIME: 'All Time',
};
const FLOWS_PAGE_SIZE = 20;

const TABS = {
  ALL: 'all',
  ARCHIVED: 'archived',
};

const ReviewsSection = ({
  isCreateReviewOpen,
  statusFilter,
  onClearStatusFilter,
  onViewReview,
  onCreateReviewToggle,
  onEditReview,
  onRestoreReview,
  restoringReviewUuid,
  activeRefreshKey,
  archivedRefreshKey,
}) => {
  const dispatch = useDispatch();
  const { selectedWorkspace, isViewer, isOwner, isLoadingWorkspaces } = useWorkspace();

  const [searchTerm, setSearchTerm] = useState('');
  const [timeFilter, setTimeFilter] = useState(TIME_FILTERS.ALL_TIME);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const pollingIntervalRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Tabs
  const [selectedTab, setSelectedTab] = useState(TABS.ALL);
  const [archivedReviews, setArchivedReviews] = useState([]);
  const [isLoadingArchived, setIsLoadingArchived] = useState(true);
  const [archivedCurrentPage, setArchivedCurrentPage] = useState(1);
  const [archivedTotalCount, setArchivedTotalCount] = useState(0);

  const designReviewCounts = useSelector(state => state.designReview.designReviewCounts);
  const designReviewHistory = useSelector(state => state.designReview.designReviewHistory);
  const reviewCount = useSelector(state => state.designReview.reviewCount);
  const tokenRefreshTimestamp = useSelector(state => state.userStore.tokenRefreshTimestamp);

  // Map time filter to days
  const getDaysFromFilter = filter => {
    switch (filter) {
      case TIME_FILTERS.THIS_MONTH:
        return new Date().getDate();
      case TIME_FILTERS.PAST_24_HOURS:
        return 1;
      case TIME_FILTERS.LAST_7_DAYS:
        return 7;
      case TIME_FILTERS.LAST_14_DAYS:
        return 14;
      case TIME_FILTERS.LAST_30_DAYS:
        return 30;
      case TIME_FILTERS.ALL_TIME:
        return 36500;
      default:
        return 30;
    }
  };

  // Check if a review needs polling (processing and created within last hour)
  const needsPolling = review => {
    if (
      review.review_status === DESIGN_REVIEW_STATUS.FETCHING_FRAME
      || review.review_status === DESIGN_REVIEW_STATUS.ANALYSING_SECTIONS
      || review.review_status === DESIGN_REVIEW_STATUS.ANALYSING_DESIGN
      || review.review_status === DESIGN_REVIEW_STATUS.SUMMARISING_FEEDBACK
    ) {
      const createdAt = new Date(review.created_at);
      const now = new Date();
      const hourInMs = 60 * 60 * 1000;

      return (now - createdAt) < hourInMs;
    }

    return false;
  };

  const hasReviewsNeedingPolling = useMemo(
    () => designReviewHistory?.results?.some(review => needsPolling(review)) || false,
    [designReviewHistory?.results]
  );

  const getScoreFilterForAPI = () => {
    if (statusFilter === null) {
      return null;
    }

    return statusFilter;
  };

  // Fetch active design review history
  const fetchDesignReviewHistory = async (
    page = 1,
    days = getDaysFromFilter(TIME_FILTERS.ALL_TIME),
    updateLoading = true,
    search = null,
    scoreFilter = null
  ) => {
    if (updateLoading) {
      setIsLoading(true);
    }

    try {
      await dispatch(
        getDesignReviewHistoryAction(
          days,
          page,
          FLOWS_PAGE_SIZE,
          PLATFORM,
          search,
          scoreFilter,
          selectedWorkspace?.uuid
        )
      );
    }
    finally {
      if (updateLoading) {
        setIsLoading(false);
      }
    }
  };

  // Fetch archived reviews into local state (does not overwrite active Redux state)
  const fetchArchivedReviews = async (
    page = 1,
    search = null,
    days = 36500,
    scoreFilter = null,
    updateLoading = false
  ) => {
    if (updateLoading) {
      setIsLoadingArchived(true);
    }

    try {
      const data = await dispatch(
        getArchivedReviewsAction(selectedWorkspace?.uuid, page, FLOWS_PAGE_SIZE, days, search, scoreFilter)
      );

      setArchivedReviews(data?.results || []);
      setArchivedTotalCount(data?.count || 0);
    }
    catch {
      setArchivedReviews([]);
      setArchivedTotalCount(0);
    }
    finally {
      setIsLoadingArchived(false);
    }
  };

  // Handle search with debouncing
  const handleSearchChange = e => {
    const newSearchTerm = e.target.value;

    setSearchTerm(newSearchTerm);
    setCurrentPage(1);
    setArchivedCurrentPage(1);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      const days = getDaysFromFilter(timeFilter);
      const scoreFilter = getScoreFilterForAPI();

      fetchDesignReviewHistory(1, days, true, newSearchTerm, scoreFilter);
      fetchArchivedReviews(1, newSearchTerm, days, scoreFilter);
    }, 500);
  };

  // Reset state when workspace changes
  useEffect(() => {
    setSearchTerm('');
    setCurrentPage(1);
    setTimeFilter(TIME_FILTERS.ALL_TIME);
    setSelectedTab(TABS.ALL);
    setArchivedReviews([]);
    setArchivedTotalCount(0);
    setArchivedCurrentPage(1);
    setIsLoadingArchived(true);
  }, [selectedWorkspace?.uuid]);

  // Fetch active reviews when workspace/page/filter changes
  useEffect(() => {
    if (isLoadingWorkspaces) {
      return;
    }

    const days = getDaysFromFilter(timeFilter);

    fetchDesignReviewHistory(currentPage, days, true, searchTerm, getScoreFilterForAPI());
  }, [dispatch, currentPage, timeFilter, statusFilter, selectedWorkspace?.uuid, isLoadingWorkspaces]);

  // Fetch archived reviews on workspace load, page change, or filter change (owners only)
  useEffect(() => {
    if (isLoadingWorkspaces || !isOwner) {
      setIsLoadingArchived(false);

      return;
    }

    const days = getDaysFromFilter(timeFilter);

    fetchArchivedReviews(archivedCurrentPage, searchTerm, days, getScoreFilterForAPI());
  }, [selectedWorkspace?.uuid, isLoadingWorkspaces, isOwner, archivedCurrentPage, timeFilter, statusFilter]);

  // Respond to external active refresh trigger (after archive/restore/rename)
  useEffect(() => {
    if (activeRefreshKey === 0 || isLoadingWorkspaces) {
      return;
    }

    const days = getDaysFromFilter(timeFilter);

    fetchDesignReviewHistory(currentPage, days, false, searchTerm, getScoreFilterForAPI());
  }, [activeRefreshKey]);

  // Respond to external archived refresh trigger (archive/unarchive actions — always show loading)
  useEffect(() => {
    if (archivedRefreshKey === 0 || isLoadingWorkspaces || !isOwner) {
      return;
    }

    const days = getDaysFromFilter(timeFilter);

    fetchArchivedReviews(archivedCurrentPage, searchTerm, days, getScoreFilterForAPI(), true);
  }, [archivedRefreshKey]);

  // Auto-switch to active tab when archived list becomes empty
  useEffect(() => {
    if (archivedReviews.length === 0 && selectedTab === TABS.ARCHIVED) {
      setSelectedTab(TABS.ALL);
    }
  }, [archivedReviews.length]);

  // Status filter changes — reset both tabs to page 1 (archived re-fetch handled by its own effect)
  useEffect(() => {
    if (statusFilter !== null) {
      setCurrentPage(1);
      setArchivedCurrentPage(1);
      const days = getDaysFromFilter(timeFilter);

      fetchDesignReviewHistory(1, days, true, searchTerm, getScoreFilterForAPI());
    }
  }, [statusFilter]);

  // Polling for processing reviews
  useEffect(() => {
    const startPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      pollingIntervalRef.current = setInterval(() => {
        if (!isCreateReviewOpen) {
          const days = getDaysFromFilter(timeFilter);

          fetchDesignReviewHistory(currentPage, days, false, searchTerm, getScoreFilterForAPI());
          dispatch(getDesignReviewCountsAction(PLATFORM, selectedWorkspace?.uuid));
        }
      }, 10000);
    };

    const stopPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };

    if (hasReviewsNeedingPolling) {
      startPolling();
    }
    else {
      stopPolling();
    }

    return stopPolling;
  }, [hasReviewsNeedingPolling, currentPage, timeFilter, isCreateReviewOpen, searchTerm, statusFilter]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }, []);

  // Token refresh → refetch active
  useEffect(() => {
    if (isLoadingWorkspaces || !tokenRefreshTimestamp) {
      return;
    }

    const days = getDaysFromFilter(timeFilter);

    fetchDesignReviewHistory(currentPage, days);
  }, [tokenRefreshTimestamp, currentPage, timeFilter, isLoadingWorkspaces]);

  const handleTimeFilterChange = newFilter => {
    setTimeFilter(newFilter);
    setCurrentPage(1);
    setArchivedCurrentPage(1);
  };

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const getStatusFilterDisplay = () => {
    switch (statusFilter) {
      case SCORE_FILTERS.LOW:
        return 'Low scores';
      case SCORE_FILTERS.GOOD:
        return 'Good scores';
      case SCORE_FILTERS.GREAT:
        return 'Great scores';
      default:
        return null;
    }
  };

  const noAuditsFoundMessage = useMemo(() => {
    const scoreFilterMap = {
      [SCORE_FILTERS.LOW]: 'low score',
      [SCORE_FILTERS.GOOD]: 'good score',
      [SCORE_FILTERS.GREAT]: 'great score',
    };

    const timeFilterMap = {
      [TIME_FILTERS.THIS_MONTH]: 'this month',
      [TIME_FILTERS.PAST_24_HOURS]: 'in the past 24 hours',
      [TIME_FILTERS.LAST_7_DAYS]: 'in the last 7 days',
      [TIME_FILTERS.LAST_14_DAYS]: 'in the last 14 days',
      [TIME_FILTERS.LAST_30_DAYS]: 'in the last 30 days',
      [TIME_FILTERS.ALL_TIME]: 'to date',
    };

    const selectedScore = scoreFilterMap[statusFilter];
    const selectedTime = timeFilterMap[timeFilter] || timeFilter.toLowerCase();

    if (selectedScore) {
      return `There are no ${selectedScore} audits ${selectedTime}.`;
    }

    return `There are no audits ${selectedTime}.`;
  }, [statusFilter, timeFilter]);

  const totalPages = Math.ceil(designReviewHistory.count / FLOWS_PAGE_SIZE);
  const startItem = (currentPage - 1) * FLOWS_PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * FLOWS_PAGE_SIZE, designReviewHistory.count);

  const hasNoReviews = useMemo(() => {
    if (typeof reviewCount?.reviews_done === 'number') {
      return reviewCount.reviews_done === 0;
    }

    return (designReviewCounts?.total_screens_analyzed ?? 0) === 0;
  }, [reviewCount, designReviewCounts]);

  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    }
    else if (currentPage <= 3) {
      for (let i = 1; i <= 4; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(totalPages);
    }
    else if (currentPage >= totalPages - 2) {
      pages.push(1);
      pages.push('...');

      for (let i = totalPages - 3; i <= totalPages; i++) {
        pages.push(i);
      }
    }
    else {
      pages.push(1);
      pages.push('...');

      for (let i = currentPage - 1; i <= currentPage + 1; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  }, [currentPage, totalPages]);

  // Derived tab visibility — only owners can see archived tab
  const showArchivedTab = isOwner && !isLoadingArchived && archivedTotalCount > 0;
  const showArchivedSkeleton = isOwner && isLoadingArchived;

  const archivedTotalPages = Math.ceil(archivedTotalCount / FLOWS_PAGE_SIZE);
  const archivedStartItem = (archivedCurrentPage - 1) * FLOWS_PAGE_SIZE + 1;
  const archivedEndItem = Math.min(archivedCurrentPage * FLOWS_PAGE_SIZE, archivedTotalCount);

  const archivedPageNumbers = useMemo(() => {
    const pages = [];
    const maxVisiblePages = 5;

    if (archivedTotalPages <= maxVisiblePages) {
      for (let i = 1; i <= archivedTotalPages; i++) {
        pages.push(i);
      }
    }
    else if (archivedCurrentPage <= 3) {
      for (let i = 1; i <= 4; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(archivedTotalPages);
    }
    else if (archivedCurrentPage >= archivedTotalPages - 2) {
      pages.push(1);
      pages.push('...');

      for (let i = archivedTotalPages - 3; i <= archivedTotalPages; i++) {
        pages.push(i);
      }
    }
    else {
      pages.push(1);
      pages.push('...');

      for (let i = archivedCurrentPage - 1; i <= archivedCurrentPage + 1; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(archivedTotalPages);
    }

    return pages;
  }, [archivedCurrentPage, archivedTotalPages]);

  return (
    <>
      {/*
       * Header: title + tabs stacked on the left, search + filter on the right.
       * align-items: flex-end puts the interactions at the same level as the tabs.
       */}
      <div className="flex items-end justify-between mb-16 gap-40">
        {/* Left column: title (top) + tabs (bottom) */}
        <div className="flex flex-col gap-16 flex-1">
          {/* Title + active filter badge */}
          <div className="flex items-center gap-12">
            <h2 className="font-inter font-semibold text-base leading-6 text-[#121723]">
              Product flows
            </h2>
            {statusFilter && selectedTab === TABS.ALL && (
              <div
                className="bg-gray-200 flex items-center px-3 py-1 gap-4 rounded-full"
              >
                <span className="font-inter text-xs text-primary-700">
                  {getStatusFilterDisplay()}
                </span>
                <button onClick={onClearStatusFilter} title="Clear filter">
                  <svg
                    className="w-4 h-4 text-primary-700"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {/* Tabs — full-width bottom border, active tab uses teal underline */}
          <div className="flex flex-row gap-4 border-b border-[#E1E4E8]">
            {/* While archived reviews are loading, replace both tab slots with a single wide skeleton */}
            {showArchivedSkeleton
              ? (
                <div className="animate-pulse rounded w-[160px] h-5 bg-[#E5E7EB] mt-2 mb-3 self-center" />
              )
              : (
                <>
                  {/* All tab */}
                  <button
                    onClick={() => setSelectedTab(TABS.ALL)}
                    className={cn(
                      'font-inter pt-2 px-3 pb-3 text-sm font-medium leading-5 bg-transparent cursor-pointer -mb-px',
                      'border-0 border-b-2',
                      selectedTab === TABS.ALL
                        ? 'text-[#13867E] border-[#28BDAE] rounded-none'
                        : 'text-[#606776] border-transparent rounded-md'
                    )}
                  >
                    All
                  </button>

                  {/* Archived tab — only shown for owners when archived reviews exist */}
                  {showArchivedTab && (
                    <button
                      onClick={() => setSelectedTab(TABS.ARCHIVED)}
                      className={cn(
                        'font-inter pt-2 px-3 pb-3 text-sm font-medium leading-5 bg-transparent cursor-pointer',
                        '-mb-px border-0 border-b-2',
                        selectedTab === TABS.ARCHIVED
                          ? 'text-[#13867E] border-[#28BDAE] rounded-none'
                          : 'text-[#606776] border-transparent rounded-md'
                      )}
                    >
                      Archived
                    </button>
                  )}
                </>
              )}
          </div>
        </div>

        {/* Right: Search + time filter */}
        {!showArchivedSkeleton && (
          <div className="flex items-center gap-8">
            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={handleSearchChange}
                className="font-inter focus:outline-none w-[200px] !pl-10 h-10 py-3 px-4 border border-[#CBCFD6]
                rounded-lg text-sm text-[#121723]"
              />
              <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none pl-3">
                <img src={ReviewsMagnifyIcon} width={20} height={20} alt="Search" />
              </div>
            </div>

            {/* Time filter */}
            <div className="flex items-center bg-white w-[200px] h-10 p-3 border border-[#D1D5DB] rounded-lg gap-8">
              <img src={ReviewsSearchTimer} alt="Time Filter" width={20} height={20} />
              <select
                value={timeFilter}
                onChange={e => handleTimeFilterChange(e.target.value)}
                className="font-inter flex-1 bg-transparent focus:outline-none text-sm text-[#121723] border-none
                appearance-none"
              >
                <option value={TIME_FILTERS.THIS_MONTH}>
                  {TIME_FILTERS.THIS_MONTH}
                </option>
                <option value={TIME_FILTERS.PAST_24_HOURS}>
                  {TIME_FILTERS.PAST_24_HOURS}
                </option>
                <option value={TIME_FILTERS.LAST_7_DAYS}>
                  {TIME_FILTERS.LAST_7_DAYS}
                </option>
                <option value={TIME_FILTERS.LAST_14_DAYS}>
                  {TIME_FILTERS.LAST_14_DAYS}
                </option>
                <option value={TIME_FILTERS.LAST_30_DAYS}>
                  {TIME_FILTERS.LAST_30_DAYS}
                </option>
                <option value={TIME_FILTERS.ALL_TIME}>
                  {TIME_FILTERS.ALL_TIME}
                </option>
              </select>
              <img src={dropDownIcon} alt="Dropdown" width={18} height={18} />
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col overflow-auto">

        {/* ---- ACTIVE TAB ---- */}
        {selectedTab === TABS.ALL && (
          isLoading
            ? (
              <div className="bg-white rounded-lg overflow-hidden flex-1 flex justify-center items-center">
                <div className="flex justify-center items-center flex-1">
                  <ImpulseSpinner frontColor={'#28BDAE'} />
                </div>
              </div>
            )
            : (
              !designReviewHistory.results?.length || designReviewHistory.results?.length === 0
                ? (
                  <div
                    className="flex flex-1 flex-col justify-center items-center py-16 overflow-auto gap-40 2xl:gap-40"
                  >
                    <img
                      src={hasNoReviews ? noReviews : noReviewsFound}
                      className="w-36 2xl:w-52 h-auto"
                    />
                    <div className="flex flex-col items-center gap-16 2xl:gap-16 text-center">
                      <span className="font-inter text-lg text-gray-900 font-semibold">
                        {hasNoReviews ? 'No audits in this workspace yet' : 'No audits found'}
                      </span>
                      <span className="font-md text-gray-900">
                        {hasNoReviews
                          ? (isViewer
                            ? 'Audits created by workspace members will appear here'
                            : 'Create your first audit now')
                          : noAuditsFoundMessage}
                      </span>
                    </div>
                    {hasNoReviews && !isViewer && (
                      <button
                        onClick={handleNewReview}
                        className="flex items-center justify-center px-6 py-3.5 bg-primary-500 text-white
                          font-inter text-base leading-6 font-medium rounded-lg shadow-shadow-xs
                          hover:bg-primary-600 transition-colors"
                      >
                        New audit
                      </button>
                    )}
                  </div>
                )
                : (
                  <div className="bg-white rounded-lg flex flex-col flex-1 overflow-auto">
                    {/* Active table header */}
                    <div className="flex items-center gap-16 px-16 py-12 border-b border-gray-200">
                      <div className="w-[26px] flex-shrink-0 flex items-center">
                        <span className="font-inter text-sm font-medium text-figma-gray-500 leading-5">
                          NPS
                        </span>
                      </div>
                      <div className="flex-[4] flex items-center">
                        <span className="font-inter text-sm font-medium text-figma-gray-500 leading-5">
                          Flow
                        </span>
                      </div>
                      <div className="flex-[2] flex">
                        <span className="font-inter text-sm font-medium text-figma-gray-500 leading-5">
                          Status
                        </span>
                      </div>
                      <div className="flex-[2] flex items-center">
                        <span className="font-inter text-sm font-medium text-figma-gray-500 leading-5">
                          Screen location
                        </span>
                      </div>
                      <div className="flex-[2] flex items-center">
                        <span className="font-inter text-sm font-medium text-figma-gray-500 leading-5">
                          Last updated
                        </span>
                      </div>
                      <div className="flex-[2] flex items-center">
                        <span className="font-inter text-sm font-medium text-figma-gray-500 leading-5">
                          Created by
                        </span>
                      </div>
                      {/* Spacer for Edit column */}
                      {isOwner && <div className="w-12 flex-shrink-0" />}
                    </div>

                    <div className="flex flex-col flex-1 overflow-auto">
                      {designReviewHistory?.results?.map(review => (
                        <ReviewRow
                          key={review.uuid}
                          review={review}
                          onView={onViewReview}
                          onEdit={onEditReview}
                          isOwner={isOwner}
                          isPolling={needsPolling(review)}
                        />
                      ))}
                    </div>

                    {/* Pagination */}
                    {designReviewHistory.count > 0 && (
                      <div className="flex items-center justify-between pr-16 py-12 border-t border-gray-200 mt-auto">
                        <div className="font-inter text-sm text-figma-gray-600">
                          Showing
                          {' '}
                          <span className="font-semibold">
                            {startItem}
                            -
                            {endItem}
                          </span>
                          {' '}
                          of
                          {' '}
                          <span className="font-semibold">
                            {designReviewHistory.count}
                          </span>
                        </div>
                        <div className="flex items-center border border-[#CBCFD6] rounded-md">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="px-3 py-2 text-sm font-medium text-figma-gray-600
                              hover:text-figma-gray-900 disabled:opacity-50"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 19l-7-7 7-7"
                              />
                            </svg>
                          </button>

                          {pageNumbers.map((page, index) => {
                            const pageClass = cn(
                              'flex px-3 py-[0.375rem] flex-col justify-center items-center text-sm font-medium',
                              {
                                'bg-primary-400 text-white': page === currentPage,
                                'bg-white text-figma-gray-400 cursor-default': page === '...',
                                'bg-white text-figma-gray-600 hover:text-figma-gray-900': (
                                  page !== currentPage && page !== '...'
                                ),
                              }
                            );

                            return (
                              <button
                                key={index}
                                onClick={() => typeof page === 'number' && handlePageChange(page)}
                                disabled={page === '...'}
                                className={pageClass}
                              >
                                {page}
                              </button>
                            );
                          })}

                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="px-3 py-2 text-sm font-medium
                              text-figma-gray-600 hover:text-figma-gray-900 disabled:opacity-50"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )
            )
        )}

        {/* ---- ARCHIVED TAB ---- */}
        {selectedTab === TABS.ARCHIVED && (
          archivedReviews.length === 0
            ? (
              <div className="flex flex-1 flex-col justify-center items-center py-16 gap-16">
                <img src={noReviewsFound} className="w-36 2xl:w-52 h-auto" />
                <div className="flex flex-col items-center gap-16 text-center">
                  <span className="font-inter text-lg text-gray-900 font-semibold">
                    No archived flows
                  </span>
                  <span className="font-md text-gray-900">
                    Archived flows will appear here.
                  </span>
                </div>
              </div>
            )
            : (
              <div className="bg-white rounded-lg flex flex-col flex-1 overflow-auto">
                {/* Archived table header */}
                <div className="flex items-center gap-16 px-16 py-12 border-b border-gray-200">
                  <div className="w-[26px] flex-shrink-0 flex items-center">
                    <span className="font-inter text-sm font-medium text-figma-gray-500 leading-5">
                      NPS
                    </span>
                  </div>
                  <div className="flex-[4] flex items-center">
                    <span className="font-inter text-sm font-medium text-figma-gray-500 leading-5">
                      Flow
                    </span>
                  </div>
                  <div className="flex-[2] flex items-center">
                    <span className="font-inter text-sm font-medium text-figma-gray-500 leading-5">
                      Screen location
                    </span>
                  </div>
                  <div className="flex-[2] flex items-center">
                    <span className="font-inter text-sm font-medium text-figma-gray-500 leading-5">
                      Last updated
                    </span>
                  </div>
                  <div className="flex-[2] flex items-center">
                    <span className="font-inter text-sm font-medium text-figma-gray-500 leading-5">
                      Created by
                    </span>
                  </div>
                  {/* Spacer for Restore column */}
                  <div className="w-16 flex-shrink-0" />
                </div>

                <div className="flex flex-col flex-1 overflow-auto">
                  {archivedReviews.map(review => (
                    <ReviewRow
                      key={review.uuid}
                      review={review}
                      isArchived
                      onView={onViewReview}
                      onRestore={onRestoreReview}
                      isRestoring={restoringReviewUuid === review.uuid}
                    />
                  ))}
                </div>

                {/* Archived pagination */}
                {archivedTotalCount > 0 && (
                  <div className="flex items-center justify-between pr-16 py-12 border-t border-gray-200 mt-auto">
                    <div className="font-inter text-sm text-figma-gray-600">
                      Showing
                      {' '}
                      <span className="font-semibold">
                        {archivedStartItem}
                        -
                        {archivedEndItem}
                      </span>
                      {' '}
                      of
                      {' '}
                      <span className="font-semibold">
                        {archivedTotalCount}
                      </span>
                    </div>
                    <div className="flex items-center border border-[#CBCFD6] rounded-md">
                      <button
                        onClick={() => setArchivedCurrentPage(p => p - 1)}
                        disabled={archivedCurrentPage === 1}
                        className="px-3 py-2 text-sm font-medium text-figma-gray-600
                          hover:text-figma-gray-900 disabled:opacity-50"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                          />
                        </svg>
                      </button>

                      {archivedPageNumbers.map((page, index) => {
                        const pageClass = cn(
                          'flex px-3 py-[0.375rem] flex-col justify-center items-center text-sm font-medium',
                          {
                            'bg-primary-400 text-white': page === archivedCurrentPage,
                            'bg-white text-figma-gray-400 cursor-default': page === '...',
                            'bg-white text-figma-gray-600 hover:text-figma-gray-900': (
                              page !== archivedCurrentPage && page !== '...'
                            ),
                          }
                        );

                        return (
                          <button
                            key={index}
                            onClick={() => typeof page === 'number' && setArchivedCurrentPage(page)}
                            disabled={page === '...'}
                            className={pageClass}
                          >
                            {page}
                          </button>
                        );
                      })}

                      <button
                        onClick={() => setArchivedCurrentPage(p => p + 1)}
                        disabled={archivedCurrentPage === archivedTotalPages}
                        className="px-3 py-2 text-sm font-medium
                          text-figma-gray-600 hover:text-figma-gray-900 disabled:opacity-50"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
        )}
      </div>
    </>
  );

  function handleNewReview() {
    onCreateReviewToggle(true);
  }
};

ReviewsSection.propTypes = {
  isCreateReviewOpen: PropTypes.bool.isRequired,
  statusFilter: PropTypes.string,
  onClearStatusFilter: PropTypes.func.isRequired,
  onViewReview: PropTypes.func.isRequired,
  onCreateReviewToggle: PropTypes.func.isRequired,
  onEditReview: PropTypes.func.isRequired,
  onRestoreReview: PropTypes.func.isRequired,
  restoringReviewUuid: PropTypes.string,
  activeRefreshKey: PropTypes.number.isRequired,
  archivedRefreshKey: PropTypes.number.isRequired,
};

export default ReviewsSection;
